import React, { Fragment, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { 
  Box, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Paper, Checkbox, FormControlLabel, Popover, Button, 
  Typography, MenuItem, Select, TextField, InputAdornment 
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { getValueByKey, displayDateTimeByLanguage } from "../../Utils/utils";
import SideBar from "../Sidebar/SideBar";
import ComposersPagePreviewCard from "./ComposersPagePreviewCard";
import NoSheets from "../NotFound/NoSheets";
import axios from "axios";
import "./ComposersPage.css";

import {
  getComposerPage,
  incrementComposerPage,
  setComposerPage,
  decrementComposerPage,
  setComposerSortOrder,
  setComposerViewMode,
  setComposerPageSize,
  resetData
} from "../../Redux/Actions/dataActions";

function ComposersPage({
  getComposerPage, composerPages, incrementComposerPage, setComposerPage, decrementComposerPage,
  composerPage, composerSortOrder, composerViewMode, setComposerSortOrder,
  setComposerViewMode, entrysPerComposerPage, setComposerPageSize, userData, dispatch, resetData, props
}) {
	
  const { t } = useTranslation();
  const userLang = userData?.language || 'de';
  const [composerOptions, setComposerOptions] = useState([]);

  useEffect(() => {
    if (!userLang) return;
    // Definition der parallelen Requests
    const fetchArtistTypes = axios.get(`/settings/options?type=artisttype&lang=${userLang}`);
    const fetchGenders     = axios.get(`/settings/options?type=gender&lang=${userLang}`);

    // Promise.all feuert beide Abfragen zeitgleich ab (schneller!)
    Promise.all([fetchArtistTypes, fetchGenders])
      .then(([artistRes, genderRes]) => {
        const artistData = artistRes.data || [];
        const genderData = genderRes.data || [];

        // 👍 Hier werden beide Arrays zu einem einzigen flachen Array zusammengemischt
        const combinedOptions = [...artistData, ...genderData];
        setComposerOptions(combinedOptions);
      })
      .catch(err => {
        console.error("Fehler beim parallelen Laden der Dropdown-Optionen:", err);
      });

  }, [userLang]); 
  
	// ⭐ MASTER-DEFINITION: Steuert alle Komponisten-Spalten & Sortier-Algorithmen
	const FIELD_DEFINITIONS = {
	  name: { 
		label: t("common.composer"), type: "text", 
		sort: (a, b, asc) => asc ? (a.name || "").localeCompare(b.name || "") : (b.name || "").localeCompare(a.name || "") 
	  },
	  epoch: { 
		label: t("composer.epoch"), type: "text", 
		sort: (a, b, asc) => asc ? (a.epoch || "").localeCompare(b.epoch || "") : (b.epoch || "").localeCompare(a.epoch || "") 
	  },
	   countryCode: { 
		label: t("composer.countryCode"), type: "text", 
		sort: (a, b, asc) => asc ? (a.countryCode || "").localeCompare(b.countryCode || "") : (b.countryCode || "").localeCompare(a.countryCode || "") 
	  },
	   gender: { 
		label: t("composer.gender"), type: "dropdown", optionType: "gender", 
		sort: (a, b, asc) => asc ? (a.gender || "").localeCompare(b.gender || "") : (b.gender || "").localeCompare(a.gender || "") 
	  },
	/*  lifespanBegin: { 
		label: "Beginn", type: "date", 
		sort: (a, b, asc) => asc ? new Date(a.lifespanBegin) - new Date(b.lifespanBegin) : new Date(b.lifespanBegin) - new Date(a.lifespanBegin) 
	  } 
	  lifespanEnd: { 
		label: "Ende", type: "date", 
		sort: (a, b, asc) => asc ? new Date(a.lifespanEnd) - new Date(b.lifespanEnd) : new Date(b.lifespanEnd) - new Date(a.lifespanEnd) 
	  } */
	   area: { 
		label:  t("composer.area"), type: "text", 
		sort: (a, b, asc) => asc ? (a.area || "").localeCompare(b.area || "") : (b.area || "").localeCompare(a.area || "") 
	  },
	   type: { 
		label:  t("composer.type"), type: "dropdown", optionType: "artisttype", 
		sort: (a, b, asc) => asc ? (a.type || "").localeCompare(b.type || "") : (b.type || "").localeCompare(a.type || "") 
	  },
	  updatedAt: { 
		label: t("common.updatedAt"), type: "datetime", 
		sort: (a, b, asc) => asc ? new Date(a.updatedAt) - new Date(b.updatedAt) : new Date(b.updatedAt) - new Date(a.updatedAt) 
	  } 
	};

  const [loading, setLoading] = useState(true);
  const [selectedLetter, setSelectedLetter] = useState("Alle");
  const [anchorEl, setAnchorOpen] = useState(null);
  const history = useHistory();
  
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

// ⭐ STABILER ZUSTAND FÜR COMPOSERS: Sofort klickbar und cache-gestützt
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const localUser = JSON.parse(localStorage.getItem("user"));
    if (localUser && localUser.composerColumns) {
      try {
        const parsed = JSON.parse(localUser.composerColumns);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {}
    }
    return ["name", "epoch"]; // Sicherer Fallback
  });

  // ⭐ LIVE-ABFRAGE FÜR INTERPRETEN: Hebelt jeden Redux-Cache-Verzug dauerhaft aus!
  useEffect(() => {
    if (userData && userData.id) {
      axios.get(`/users/info/${userData.id}`)
        .then((res) => {
          if (res.data) {
            if (res.data.composerSort) {
              setComposerSortOrder(res.data.composerSort);
            }
            if (res.data.composerColumns) {
              try {
                const parsed = JSON.parse(res.data.composerColumns);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  setVisibleColumns(parsed); // Setzt die Kometen-Haken live auf DB-Wahrheit!
                }
              } catch (e) {}
            }
          }
        })
        .catch(err => console.error("Fehler beim Live-Layout-Abruf für Composers:", err));
    }
  }, [userData, setComposerSortOrder]);

  const alphabet = [t("common.all"), ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];

  const getData = useCallback(() => {
    if (composerPage === undefined || composerPage < 1) setComposerPage(1);
    const data = { page: composerPage, sortBy: "updatedAt desc" };

    setLoading(true);
    getComposerPage(data, () => setLoading(false));
  }, [composerPage, getComposerPage, setComposerPage]);

  useEffect(() => {
    getData();
    document.title = `SheetAble - ${t("PageComposersPage.pageTitle")}`;
  }, [getData, t]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debouncedQuery.trim() === "") {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    axios.get(`/search/composers/${encodeURIComponent(debouncedQuery.trim())}`)
      .then((res) => {
        if (res.data) setSearchResults(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Fehler bei der Live-Suche:", err);
        setLoading(false);
      });
  }, [debouncedQuery]);

  const saveLayoutToDatabase = (newSort, newCols) => {
    if (!userData || !userData.id) return;

    const finalSort = newSort || composerSortOrder;
    const finalCols = newCols || visibleColumns;

    const payload = {
      composerSort: finalSort,
      composerColumns: JSON.stringify(finalCols)
    };

    // ⭐ FIX: Sofortiger Redux-Push für die Komponisten, damit beim Wechseln nichts flackert!
    if (dispatch) {
      dispatch({
        type: "SET_USER_DATA",
        payload: {
          ...userData,
          composerSort: finalSort,
          composerColumns: JSON.stringify(finalCols)
        }
      });
    }
    console.log("[saveLayoutToDatabase] Aktueller Wert:", payload);

    axios.put(`/users/layout/${userData.id}`, payload)
      .then((res) => {
        if (dispatch) dispatch({ type: "SET_USER_DATA", payload: res.data });
        localStorage.setItem("user", JSON.stringify(res.data));
      })
      .catch(err => console.error("Sync-Fehler bei Composers:", err));
  };

  const handleColumnToggle = (columnId) => {
    let updatedCols = visibleColumns.includes(columnId)
      ? visibleColumns.filter(id => id !== columnId)
      : [...visibleColumns, columnId];
    
    if (updatedCols.length === 0) return;

    let finalSort = composerSortOrder;
    if (!updatedCols.some(id => composerSortOrder.startsWith(id))) {
      finalSort = updatedCols[0] + "_asc";
      setComposerSortOrder(finalSort);
    }

    setVisibleColumns(updatedCols);

    if (dispatch) {
      dispatch({
        type: "SET_USER_DATA",
        payload: {
          ...userData,
          composerSort: finalSort,
          composerColumns: JSON.stringify(updatedCols)
        }
      });
    }

    saveLayoutToDatabase(finalSort, updatedCols);
  };

  const handleHeaderClick = (columnId) => {
    let newSortOrder = `${columnId}_asc`;
    if (composerSortOrder === `${columnId}_asc`) {
      newSortOrder = `${columnId}_desc`;
    } else if (composerSortOrder === `${columnId}_desc`) {
      newSortOrder = `${columnId}_asc`;
    }
    setComposerSortOrder(newSortOrder);
    saveLayoutToDatabase(newSortOrder, visibleColumns);
  };

  const dynamicSortOptions = [];
  visibleColumns.forEach((colId) => {
    if (FIELD_DEFINITIONS[colId]) {
      dynamicSortOptions.push({ value: `${colId}_asc`, label: `${FIELD_DEFINITIONS[colId].label} (${t("common.asc")})` });
      dynamicSortOptions.push({ value: `${colId}_desc`, label: `${FIELD_DEFINITIONS[colId].label} (${t("common.desc")})` });
    }
  });

  const sourceComposers = debouncedQuery.trim() !== "" 
    ? searchResults 
    : (composerPages && composerPages[composerPage] ? composerPages[composerPage].filter(Boolean) : []);

  // ⭐ BERECHNUNG AKTIVER BUCHSTABEN: Prüft, welche Buchstaben reale Interpreten besitzen
  const existingLetters = new Set(
    sourceComposers
      .map((c) => c.name ? c.name.trim().charAt(0).toUpperCase() : "")
      .filter(Boolean)
  );
  
  // Filterlogik für A–Z anwenden
  const filteredComposers = selectedLetter === "Alle"
    ? sourceComposers
    : sourceComposers.filter((c) => c.name && c.name.toUpperCase().startsWith(selectedLetter));

  // ⭐ DYNAMISCHE EXEKUTION DER SORTIERUNG
  let sortedComposers = [...filteredComposers];
  let activeSortOpt = dynamicSortOptions.find(opt => opt.value === composerSortOrder);
  
  if (!activeSortOpt && dynamicSortOptions.length > 0) {
    activeSortOpt = dynamicSortOptions[0];
  }

  if (activeSortOpt) {
    const isAsc = activeSortOpt.value.endsWith("_asc");
    const activeFieldId = activeSortOpt.value.replace(isAsc ? "_asc" : "_desc", "");
    if (FIELD_DEFINITIONS[activeFieldId]) {
      sortedComposers.sort((a, b) => FIELD_DEFINITIONS[activeFieldId].sort(a, b, isAsc));
    }
  }

   // 4. Dynamische Gesamtseiten basierend auf den gefilterten Daten und der Seitengröße berechnen
  const calculatedTotalPages = Math.ceil(sortedComposers.length / (entrysPerComposerPage || 10)) || 1;

  // Sicherheitscheck: Falls die aktuelle Seite durch Filtern/Skalieren außerhalb des Bereichs liegt
  useEffect(() => {
    if (composerPage > calculatedTotalPages) {
      setComposerPage(calculatedTotalPages);
    }
  }, [calculatedTotalPages, composerPage, setComposerPage]);

  const startIndex = (composerPage - 1) * entrysPerComposerPage;
  const endIndex = startIndex + entrysPerComposerPage;

  const paginatedComposers = sortedComposers.slice(startIndex, endIndex);

  // Modifizierte Pagination-Handler, die sich nach dem neuen Limit richten
  const handlePrevPage = (e) => {
    e.preventDefault();
    if (composerPage > 1) {
      decrementComposerPage();
    }
  };

  const handleNextPage = (e) => {
    e.preventDefault();
    if (composerPage < calculatedTotalPages) {
      incrementComposerPage();
    }
  };

  const hasData = paginatedComposers.length > 0;

  return (
    <Fragment>
      <SideBar />
      <div id={hasData ? "" : "notfound"} className="home_content">
        {!loading ? (
          hasData ? (
            <div className="composer-wrapper" style={{ padding: "20px" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: "wrap", gap: "15px" }}>
                <Box display="flex" alignItems="center" gap="15px">
                  <span className="doc_sheet">{t("PageComposersPage.pageTitle")}</span>
                  <Typography variant="body2" sx={{ backgroundColor: "rgba(79, 64, 179, 0.08)", color: "#4f40b3", fontWeight: "600", px: 1.5, py: 0.5, borderRadius: "12px", fontSize: "13px" }}>
                    {sortedComposers.length} {sortedComposers.length === 1 ?  t("common.entry") : t("common.entries") }
                  </Typography>
                </Box>
                
                <TextField
                  id="permanent-composer-search-field"
                  key="global-composer-search-input"
                  size="small"
                  variant="outlined"
                  placeholder={t("PageComposersPage.placeholder_search")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ width: "320px", backgroundColor: "white", borderRadius: "8px" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "#4f40b3" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box display="flex" gap="12px" alignItems="center">
                  {debouncedQuery.trim() === "" && (
                    <Box display="flex" alignItems="center" gap="6px">
                      <Typography variant="caption" sx={{ color: "#757575", fontWeight: "600" }}>{t("common.entries")}:</Typography>
                      <Select
                        size="small"
                        value={entrysPerComposerPage || 10}
                        onChange={(e) => {
                          const newSize = parseInt(e.target.value, 10);
                          setComposerPageSize(newSize); 
                          setComposerPage(1); 
                          saveLayoutToDatabase(composerSortOrder, visibleColumns);
                        }}
                        sx={{ fontSize: "13px", backgroundColor: "white", height: "32px" }}
                      	MenuProps={{
						style: { zIndex: 99999 }, // Falls es hinter Fenstern liegt
						disablePortal: false,
						MenuListProps: {
						  style: { 
							display: "flex", 
							flexDirection: "column", // Zwingt das innere ul-Layout untereinander
							padding: "4px 0"
						  }
						}
					  }}>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </Box>
                  )}

                  {composerViewMode === "list" && (
                    <Fragment>
                      <Button variant="outlined" startIcon={<ViewColumnIcon />} onClick={(e) => setAnchorOpen(e.currentTarget)} sx={{ textTransform: "none", color: "#4f40b3", borderColor: "#4f40b3" }}>
                        {t("common.column")}
                      </Button>
                      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorOpen(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: "6px", minWidth: "180px" }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold", mb: 1, color: "#4f40b3" }}>{t("common.column")}:</Typography>
                          {Object.keys(FIELD_DEFINITIONS).map((id) => (
                            <FormControlLabel 
                              key={id} 
                              control={
                                <Checkbox 
                                  size="small" 
                                  checked={visibleColumns.includes(id)} 
                                  onChange={() => handleColumnToggle(id)} 
                                  sx={{ '&.Mui-checked': { color: '#4f40b3' } }} 
                                />
                              } 
                              label={<Typography variant="body2" style={{ color: "#1d1b31" }}>{FIELD_DEFINITIONS[id].label}</Typography>} 
                            />
                          ))}
                        </Box>
                      </Popover>
                    </Fragment>
                  )}

                  {/* Dynamisches Sortierungs-Dropdown (Eingeklappt im nativen Browser-Modus) */}
                  <Select native size="small" value={dynamicSortOptions.some(o => o.value === composerSortOrder) ? composerSortOrder : (dynamicSortOptions?.value || "")} onChange={(e) => { setComposerSortOrder(e.target.value); saveLayoutToDatabase(e.target.value, visibleColumns); }} sx={{ fontSize: "14px", backgroundColor: "white", paddingRight: "10px" }}>
                    {dynamicSortOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>

                  <Select size="small" value={composerViewMode} onChange={(e) => setComposerViewMode(e.target.value)} sx={{ fontSize: "14px", backgroundColor: "white" }}
				  	MenuProps={{
						style: { zIndex: 99999 }, // Falls es hinter Fenstern liegt
						disablePortal: false,
						MenuListProps: {
						  style: { 
							display: "flex", 
							flexDirection: "column", // Zwingt das innere ul-Layout untereinander
							padding: "4px 0"
						  }
						}
					  }}>
                    <MenuItem value="gallery">{t("common.view_mode_gallery")}</MenuItem>
                    <MenuItem value="list">{t("common.view_mode_list")}</MenuItem>
                  </Select>
                </Box>
              </Box>

              {/* Alphabetischer Filterbalken mit inaktiven Tasten */}
              <Box display="flex" flexWrap="wrap" gap="4px" sx={{ mb: 3 }}>
                {alphabet.map(l => {
                  const isAvailable = l === t("common.all") || existingLetters.has(l);
                  return (
                    <Button 
                      key={l} 
                      variant={selectedLetter === l ? "contained" : "text"} 
                      size="small" 
                      disabled={!isAvailable}
                      onClick={() => setSelectedLetter(l)} 
                      sx={{ 
                        minWidth: "30px", 
                        height: "32px", 
                        backgroundColor: selectedLetter === l ? "#4f40b3" : "transparent", 
                        color: selectedLetter === l ? "white" : (isAvailable ? "#1d1b31" : "#c0c0c0"),
                        fontWeight: isAvailable ? "600" : "normal",
                        '&.Mui-disabled': { color: '#d0d0d0', backgroundColor: 'transparent' }
                      }}
                    >
                      {l}
                    </Button>
                  );
                })}
              </Box>

              {composerViewMode === "gallery" ? (
                // Galerie-Modus mit responsivem Zeilenumbruch (flexWrap)
                <div className="middle-part-container">
                  <ul className="all-sheets-container full-width" style={{ display: "flex", flexWrap: "wrap", gap: "20px", padding: 0, margin: 0, listStyle: "none" }}>
                    {paginatedComposers.map((c) => <ComposersPagePreviewCard key={`gallery-${c.safe_name}-${c.id}`} composer={c} />)}
                  </ul>
                </div>
              ) : (
                // Interaktive Tabellen-Listenansicht mit Spalten-Header-Klicks
                <TableContainer component={Paper} sx={{ borderRadius: "12px", border: "1px solid #ececec" }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#fcfbfe" }}>
                      <TableRow>
                        {Object.keys(FIELD_DEFINITIONS).filter(id => visibleColumns.includes(id)).map(id => {
                          const isSortedThisField = composerSortOrder.startsWith(id);
                          const isAsc = composerSortOrder.endsWith("_asc");
                          return (
                            <TableCell 
                              key={id} 
                              onClick={() => handleHeaderClick(id)}
                              sx={{ 
                                fontWeight: "bold", 
                                color: "#4f40b3", 
                                py: 1.5,
                                cursor: "pointer",
                                userSelect: "none",
                                '&:hover': { backgroundColor: "rgba(79, 64, 179, 0.04)" }
                              }}
                            >
                              <Box display="flex" alignItems="center" gap="4px">
                                {FIELD_DEFINITIONS[id].label}
                                {isSortedThisField && (
                                  isAsc ? <ArrowUpwardIcon style={{ fontSize: "14px" }} /> : <ArrowDownwardIcon style={{ fontSize: "14px" }} />
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedComposers.map((c) => (
                        <TableRow key={`row-${c.safe_name}-${c.id}`} hover onClick={() => history.push(`/composer/${c.safe_name}`)} style={{ cursor: "pointer" }}>
                          {Object.keys(FIELD_DEFINITIONS).filter(id => visibleColumns.includes(id)).map(id => (
							<TableCell key={`cell-${id}-${c.safe_name}`} sx={{ py: 1.5 }}>
							  {id === "name" ? (<b>{c.name}</b>) : 
							    id === "updatedAt" ? displayDateTimeByLanguage(c.updatedAt, userLang) : 
								  FIELD_DEFINITIONS[id].type === "dropdown" ? getValueByKey(composerOptions, c[id], userLang, FIELD_DEFINITIONS[id].optionType) :
								    ( c[id] || c[id.charAt(0).toUpperCase() + id.slice(1)] || "-"
							  )}
							</TableCell> 
                         ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
			  
              {debouncedQuery.trim() === "" && (
                <Box display="flex" justifyContent="center" alignItems="center" gap="15px" sx={{ mt: 4 }}>
                  <Button variant="outlined" size="small" disabled={composerPage <= 1} onClick={handlePrevPage}>&lt;</Button>
					<Typography variant="body2">{t("common.pageCount", {startCnt: composerPage, endCnt: calculatedTotalPages})}</Typography>
                  <Button variant="outlined" size="small" disabled={composerPage >= calculatedTotalPages} onClick={handleNextPage}>&gt;</Button>
                </Box>
              )}			  

            </div>
          ) : <NoSheets />
        ) : <div className="loading">{t("PageComposersPage.loading_page")}</div>}
      </div>
    </Fragment>
  );
}

const mapStateToProps = (state) => ({
  composerPages: state.data.composerPages,
  composerPage: state.data.composerPage,
  composerSortOrder: state.data.composerSortOrder,
  composerViewMode: state.data.composerViewMode,
  entrysPerComposerPage: state.data.entrysPerComposerPage,
  userData: state.user.userData,
});

const mapActionsToProps = { 
	getComposerPage, 
	incrementComposerPage, 
	setComposerPage, 
	decrementComposerPage, 
	setComposerSortOrder, 
	setComposerViewMode, 
	setComposerPageSize,
	resetData
};
const ConnectedComposersPage = connect(mapStateToProps, mapActionsToProps)(ComposersPage);
export default ConnectedComposersPage;
